import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AGENDA_HORIZON_DAYS, addDays, daysBetween } from "../../../../../app/src/Utils/agendaDateUtils";
import { useAgendaFormat } from "../state/useAgendaFormat";

// Tira de fechas (pantallas 01/02/05, componente 16): siempre los 14 días del horizonte, de
// meta.today a meta.until, tengan o no eventos. La casilla marcada NO es un filtro: solo indica
// en qué día está el usuario y sirve para saltar a él. Los días sin eventos (según meta.days, con
// los filtros vigentes) se atenúan y no se pulsan.
//
// Supuesto: mientras no ha llegado la primera respuesta (sin meta.today), se pintan 14 casillas
// vacías y deshabilitadas en el mismo sitio, para que la barra no salte al cargar.
const DateStrip = ({ today, until, metaDays, activeDay, onSelect }) => {
  const { t } = useTranslation();
  const format = useAgendaFormat();
  const stripRef = useRef(null);

  const counts = new Map((metaDays || []).map((d) => [d.date, d.count]));
  const days = today ? daysBetween(today, until || addDays(today, AGENDA_HORIZON_DAYS - 1)) : [];

  // La casilla marcada tiene que quedar visible: al marcarla por scroll-spy o al abrir la página
  // con un día en la URL, se desplaza la tira (sin mover la página).
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || !activeDay) return;
    const tile = strip.querySelector(`[data-day="${activeDay}"]`);
    if (!tile) return;
    const left = tile.offsetLeft - strip.offsetLeft;
    const right = left + tile.offsetWidth;
    if (left < strip.scrollLeft || right > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: Math.max(0, left - 8), behavior: "smooth" });
    }
  }, [activeDay, today]);

  if (!today) {
    return (
      <div className="ag-strip" aria-hidden="true">
        {Array.from({ length: AGENDA_HORIZON_DAYS }).map((_, i) => (
          <span key={i} className="ag-tile ag-tile--placeholder" />
        ))}
      </div>
    );
  }

  return (
    <div className="ag-strip" ref={stripRef} role="group" aria-label={t("agenda.dateStrip")}>
      {days.map((day) => {
        const count = counts.get(day) || 0;
        const isActive = day === activeDay;
        const isToday = day === today;
        const disabled = count === 0;
        return (
          <button
            key={day}
            type="button"
            data-day={day}
            className={`ag-tile${isActive ? " is-active" : ""}${isToday ? " is-today" : ""}${disabled ? " is-empty" : ""}`}
            disabled={disabled}
            aria-label={format.tileAria(day, count)}
            aria-current={isActive ? "date" : undefined}
            onClick={() => onSelect(day)}
          >
            <span className="ag-tile-weekday">{isToday ? t("agenda.today") : format.tileWeekday(day)}</span>
            <span className="ag-tile-number">{format.tileNumber(day)}</span>
            <span className="ag-tile-month">{format.tileMonth(day)}</span>
          </button>
        );
      })}
    </div>
  );
};

export default DateStrip;
